import { Injectable } from '@angular/core';

import { AttributeHelper } from 'ish-core/models/attribute/attribute.helper';
import { CategoryTreeHelper } from 'ish-core/models/category-tree/category-tree.helper';
import { CategoryTree } from 'ish-core/models/category-tree/category-tree.model';
import { ImageMapper } from 'ish-core/models/image/image.mapper';
import { SeoAttributesMapper } from 'ish-core/models/seo-attributes/seo-attributes.mapper';

import { CategoryData, CategoryPathElement } from './category.interface';
import { Category, CategoryHelper } from './category.model';

@Injectable({ providedIn: 'root' })
export class CategoryMapper {
  constructor(private imageMapper: ImageMapper) {}

  /**
   * Utility Method:
   * Maps the incoming raw category path to a path with unique IDs.
   */
  mapCategoryPath(path: CategoryPathElement[]) {
    if (path?.length) {
      return path
        .map(x => x.id)
        .reduce((acc, _, idx, arr) => [...acc, arr.slice(0, idx + 1).join(CategoryHelper.uniqueIdSeparator)], []);
    }
    throw new Error('input is falsy');
  }

  /**
   * Utility Method:
   * Creates Category stubs from the category path (excluding the last element)
   */
  categoriesFromCategoryPath(path: CategoryPathElement[]): CategoryTree {
    if (!path?.length) {
      return CategoryTreeHelper.empty();
    }

    let uniqueId: string;
    const newCategoryPath: string[] = [];

    return (
      path
        // remove the last
        .filter((_, idx, arr) => idx !== arr.length - 1)
        .map(pathElement => {
          // accumulate and construct uniqueId and categoryPath
          uniqueId = !uniqueId ? pathElement.id : uniqueId + CategoryHelper.uniqueIdSeparator + pathElement.id;
          newCategoryPath.push(uniqueId);

          // yield category stub
          return {
            uniqueId,
            name: pathElement.name,
            completenessLevel: 0,
            categoryPath: [...newCategoryPath],
          };
        })
        // construct a tree from it
        .reduce((tree, cat: Category) => CategoryTreeHelper.add(tree, cat), CategoryTreeHelper.empty())
    );
  }

  /**
   * Compute completeness level of incoming raw data.
   */
  computeCompleteness(categoryData: CategoryData): number {
    if (!categoryData) {
      return -1;
    }

    // adjust CategoryCompletenessLevel.Max accordingly
    let count = 0;

    if (categoryData.categoryRef) {
      // category path categories do not contain a categoryRef
      count++;
    }
    if (categoryData.categoryPath?.length === 1) {
      // root categories have no images but a single-entry categoryPath
      count++;
    }
    if (categoryData.images) {
      // images are supplied for sub categories in the category details call
      count++;
    }
    if (categoryData.seoAttributes) {
      // seo attributes are only supplied with the category details call
      count++;
    }

    return count;
  }

  /**
   * Maps a raw {@link CategoryData} element to a {@link Category} element ignoring subcategories.
   */
  fromDataSingle(categoryData: CategoryData): Category {
    if (categoryData) {
      const categoryPath = this.mapCategoryPath(categoryData.categoryPath);
      const uniqueId = categoryPath[categoryPath.length - 1];

      return {
        uniqueId,
        categoryRef: categoryData.categoryRef,
        categoryPath,
        name: categoryData.name,
        hasOnlineProducts: categoryData.hasOnlineProducts,
        description: categoryData.description,
        images: this.imageMapper.fromImages(categoryData.images),
        attributes: categoryData.attributes,
        hideInMenu: this.shouldHideInMenu(categoryData),
        completenessLevel: this.computeCompleteness(categoryData),
        seoAttributes: SeoAttributesMapper.fromData(categoryData.seoAttributes),
      };
    } else {
      throw new Error(`'categoryData' is required`);
    }
  }

  /**
   * Converts the tree of {@link CategoryData} to the model entity {@link CategoryTree}.
   * Inserts all sub categories accordingly.
   */
  fromData(categoryData: CategoryData): CategoryTree {
    if (categoryData) {
      // recurse into tree
      let subTrees: CategoryTree;
      if (categoryData.subCategories?.length) {
        subTrees = categoryData.subCategories
          .map(c => this.fromData(c) as CategoryTree)
          .reduce((a, b) => CategoryTreeHelper.merge(a, b));
      } else {
        subTrees = CategoryTreeHelper.empty();
      }

      // create tree from current category
      const rootCat = this.fromDataSingle(categoryData);
      const tree = CategoryTreeHelper.single(rootCat);

      // create tree from categoryPath stubs
      const categoryPathTree = this.categoriesFromCategoryPath(categoryData.categoryPath);

      // merge sub categories onto current tree
      let treeWithSubCategories = CategoryTreeHelper.merge(tree, subTrees);

      // CUSTOMIZATION: Catalog roots like Shop list peer top-level departments as
      // `subCategories` with their own single-segment categoryPath (uniqueId stays
      // `HW`, not `SHOP.HW`). CategoryTreeHelper only creates edges from multi-segment
      // paths, so link those child uniqueIds onto the parent so CLP hasChildren works.
      if (rootCat.uniqueId && categoryData.subCategories?.length) {
        const existing = treeWithSubCategories.edges[rootCat.uniqueId] || [];
        const childIds = categoryData.subCategories
          .map(child => {
            try {
              return this.fromDataSingle(child)?.uniqueId;
            } catch {
              return undefined;
            }
          })
          .filter((id): id is string => !!id && !existing.includes(id));
        if (childIds.length) {
          treeWithSubCategories = {
            ...treeWithSubCategories,
            edges: {
              ...treeWithSubCategories.edges,
              [rootCat.uniqueId]: [...existing, ...childIds],
            },
          };
        }
      }

      // merge categoryPath stubs onto current tree
      return CategoryTreeHelper.merge(treeWithSubCategories, categoryPathTree);
    } else {
      throw new Error(`'categoryData' is required`);
    }
  }

  /**
   * Determines if a category should be hidden in the menu based on the 'ShowInMenu' attribute.
   * Categories without the ShowInMenu attribute or with any value other than 'false' will be shown by default.
   * The 'hideInMenu' property is always set on mapped Category objects for consistent behavior.
   */
  private shouldHideInMenu(categoryData: CategoryData): boolean {
    if (!categoryData?.attributes?.length) {
      return false;
    }

    const showInMenuValue = AttributeHelper.getAttributeValueByAttributeName<string>(
      categoryData.attributes,
      'ShowInMenu'
    );
    return showInMenuValue?.toLowerCase() === 'false';
  }
}
